/**
 * The readable text inside whatever was thrown.
 *
 * Wallets, providers and RTK Query throw plain objects at least as often as
 * they throw `Error`s, and `String({code, message})` is `"[object Object]"` —
 * which matches no decoder, reaches the user as *"Transaction failed: [object
 * Object]"*, and reaches observability as a report naming nothing. It cost one
 * holder five failed offers and a message telling us the platform had blocked
 * them, and it appeared again on the DAO vote.
 *
 * Three copies of this logic had grown in the app by then. This is the one.
 */
export function errorText(err: unknown): string {
  if (err instanceof Error) {
    const data = (err as { data?: { message?: string } }).data?.message
    return `${err.message}${data ? ` ${data}` : ''}`
  }
  if (err && typeof err === 'object') {
    const e = err as {
      message?: unknown
      reason?: unknown
      error?: { message?: unknown }
      data?: { message?: unknown }
      shortMessage?: unknown
      code?: unknown
      status?: unknown
    }
    const parts = [e.shortMessage, e.message, e.reason, e.error?.message, e.data?.message].filter(
      (p): p is string => typeof p === 'string' && p.length > 0
    )
    if (parts.length) return [...new Set(parts)].join(' ')
    /*
     * Nothing readable inside. A code or an HTTP status is still something a
     * person can quote and a reader can look up; JSON keeps whatever did
     * arrive. Anything beats "[object Object]".
     */
    const code = e.code ?? e.status
    const prefix = typeof code === 'string' || typeof code === 'number' ? `code ${code} ` : ''
    try {
      return `${prefix}${JSON.stringify(err).slice(0, 300)}`.trim()
    } catch {
      return `${prefix}unreadable error`.trim()
    }
  }
  return String(err ?? '')
}
